/**
 * In-widget debug console — shows log entries without needing browser dev tools.
 *
 * SRP: This component only renders the UI. All logging logic lives in
 * `../observability/` (Logger, sinks, context).
 *
 * Reads from the shared `memorySink` so every `getLogger(...).info(...)` call
 * in the app automatically appears here — no parallel logging path.
 *
 * The legacy `debugLog(level, message, data)` helper is preserved as a
 * thin compatibility shim so existing call sites keep working.
 */

import { makeStyles, tokens } from "@fluentui/react-components";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import { getLogger, memorySink } from "../observability";
import type { LogLevel, LogRecord } from "../observability";

// ---------------------------------------------------------------------------
// Compatibility shim — routes old debugLog() calls through the central logger.
// ---------------------------------------------------------------------------

const _shimLogger = getLogger("frontend.legacy");

export function debugLog(level: "info" | "warn" | "error", message: string, data?: unknown): void {
  const extra =
    data === undefined
      ? undefined
      : typeof data === "object" && data !== null
        ? (data as Record<string, unknown>)
        : { value: data };
  _shimLogger[level](message, extra);
}

// Re-export for legacy imports
export type LogEntry = LogRecord;

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const useStyles = makeStyles({
  toggle: {
    // Top-right, tucked under Office's host "i" overlay (which covers
    // roughly the top 32px of the right corner) and above the tab bar —
    // clear of the composer's send/stop button it used to sit on.
    position: "fixed",
    top: "33px",
    right: "16px",
    width: "14px",
    height: "14px",
    padding: 0,
    borderRadius: "3px",
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground3,
    fontSize: "9px",
    lineHeight: 1,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    opacity: 0.35,
    ":hover": { opacity: 1 },
  },
  panel: {
    position: "fixed",
    bottom: "48px",
    left: 0,
    right: 0,
    maxHeight: "200px",
    backgroundColor: "#1e1e1e",
    color: "#d4d4d4",
    fontSize: "0.6875rem",
    fontFamily: "'Courier New', monospace",
    overflowY: "auto",
    zIndex: 999,
    borderTop: "2px solid #007acc",
    padding: "4px 0",
  },
  entry: {
    padding: "1px 8px",
    lineHeight: "16px",
    whiteSpace: "pre-wrap",
    wordBreak: "break-all",
  },
  debug: { color: "var(--text-secondary)" },
  info: { color: "#d4d4d4" },
  warn: { color: "#cca700" },
  error: { color: "#f44747" },
  timestamp: { color: "#6a9955", marginRight: "6px" },
  data: { color: "#9cdcfe", marginLeft: "4px" },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "2px 8px",
    borderBottom: "1px solid #333",
    color: "#888",
    fontSize: "0.625rem",
  },
  clearBtn: {
    cursor: "pointer",
    color: "#888",
    ":hover": { color: "#fff" },
  },
});

function fmtTs(epochSec: number): string {
  const d = new Date(epochSec * 1000);
  return d.toLocaleTimeString("en-US", { hour12: false });
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const DebugConsole: React.FC = () => {
  const styles = useStyles();
  const [open, setOpen] = useState(false);
  const [tick, setTick] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    return memorySink.subscribe(() => setTick((t) => t + 1));
  }, []);

  useEffect(() => {
    if (open && tick >= 0 && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [open, tick]);

  if (!open) {
    return (
      <button type="button" className={styles.toggle} onClick={() => setOpen(true)} title="Debug console">
        {">"}
      </button>
    );
  }

  const entries = memorySink.entries();

  return (
    <div ref={scrollRef} className={styles.panel}>
      <div className={styles.header}>
        <span>Debug Console ({entries.length} entries)</span>
        <span style={{ display: "flex", gap: "0.5rem" }}>
          <button type="button" className={styles.clearBtn} onClick={() => memorySink.clear()}>
            Clear
          </button>
          <button type="button" className={styles.clearBtn} onClick={() => setOpen(false)}>
            Close
          </button>
        </span>
      </div>
      {entries.map((entry, i) => {
        const levelClass = styles[entry.level as LogLevel];
        return (
          <div key={`${entry.timestamp}_${entry.logger}_${entry.level}`} className={`${styles.entry} ${levelClass}`}>
            <span className={styles.timestamp}>{fmtTs(entry.timestamp)}</span>
            <span>
              [{entry.logger}] {entry.message}
            </span>
            {entry.extra && Object.keys(entry.extra).length > 0 && (
              <span className={styles.data}>{JSON.stringify(entry.extra).slice(0, 200)}</span>
            )}
          </div>
        );
      })}
    </div>
  );
};
